// /resources/js/app/modules/v2/app/V2UIManager.js
import { v2StateManager } from './V2StateManager.js';
import { v2ActionHandler } from './V2ActionHandler.js';
import { v2Renderer } from '../V2Renderer.js';
import { monitoringManager } from '../../MonitoringManager.js';

/**
 * @class V2UIManager
 * Manages all user interface interactions, DOM event listeners, and UI-specific logic.
 * It listens for user actions and calls the appropriate methods on the V2ActionHandler
 * to trigger application logic, acting as the bridge between the user and the app's core.
 */
class V2UIManager {
    /**
     * Sets up all the application's global event listeners.
     */
    init() {
        this.setupGlobalListeners();
        this.setupStreamNavigatorListener();
        this.setupActionListeners();
        this.setupFeedInteractionListeners();
        this.initializeMonitoringPill();
    }

    /**
     * A helper to check if the user has a valid Telegram session in their browser.
     * @returns {boolean}
     */
    _isTelegramConnected() {
        const user = window.SGNL.user;
        if (!user || !user.telegram_user_id) return false;
        const sessionKey = `gramjs_session_user_${user.id}`;
        return !!localStorage.getItem(sessionKey);
    }

    /**
     * Sets the initial state and click handler for the monitoring status pill.
     */
    initializeMonitoringPill() {
        if (!window.SGNL.user || !window.SGNL.user.id) return;

        const pill = document.getElementById('monitoring-control-pill');
        if (!pill) return;

        monitoringManager._updatePillUI(this._isTelegramConnected() ? 'offline' : 'unconnected');
        pill.addEventListener('click', () => {
            this._isTelegramConnected()
                ? monitoringManager.toggle()
                : window.location.href = '/telegram-connect';
        });
    }

    setupGlobalListeners() {
        // Pause polling when the tab is not visible to save resources.
        document.addEventListener('visibilitychange', () => {
            document.hidden ? v2ActionHandler.stopStatusPolling() : v2ActionHandler.startStatusPolling();
        });

        // Listens for a global event fired when a signal is unlocked, then refreshes the view.
        window.addEventListener('signal-unlocked', () => {
            v2ActionHandler.loadActiveStream(v2StateManager.pagination.current_page || 1);
        });

        // Listens for new signals created by the monitoring service.
        window.addEventListener('v2-optimistic-signal-created', e => {
            const newSignal = e.detail;
            if (v2StateManager.activeStreamSlug === 'my-signals') {
                v2StateManager.optimisticSignals.unshift(newSignal);
                v2ActionHandler.renderCurrentView();
            } else {
                v2StateManager.streamUpdateCounts['my-signals'] = (v2StateManager.streamUpdateCounts['my-signals'] || 0) + 1;
                v2Renderer.updateStreamTabBadge('my-signals', v2StateManager.streamUpdateCounts['my-signals']);
            }
        });
    }

    setupStreamNavigatorListener() {
        document.getElementById('stream-navigator-container').addEventListener('click', e => {
            const streamTab = e.target.closest('[data-stream-slug]');
            if (!streamTab || v2StateManager.isLoading) return;

            const newSlug = streamTab.dataset.streamSlug;
            if (newSlug !== v2StateManager.activeStreamSlug) {
                v2ActionHandler.changeStream(newSlug);
            }
        });
    }
    setupActionListeners() {
        // Handles clicks on the "Show X new" refresh banner.
        // We use optional chaining (?.) here as a safeguard, because this element
        // is also hidden by default and only appears when there are new signals.
        document.getElementById('v2-refresh-button')?.addEventListener('click', () => {
            if (!v2StateManager.isLoading) v2ActionHandler.loadActiveStream(1);
        });

        // The code block for 'v2-view-switcher' has been completely removed,
        // as the element is no longer interactive.
    }
    
    setupFeedInteractionListeners() {
        // Handles clicks on sortable table headers or card view sort buttons.
        // By attaching the listener to the body, we can catch clicks from both
        // the card sorter and the table headers using event delegation.
        document.body.addEventListener('click', e => {
            const sortTrigger = e.target.closest('[data-sort-by]');
            if (sortTrigger && !v2StateManager.isLoading) {
                v2ActionHandler.changeSort(sortTrigger.dataset.sortBy);
            }
        });
    }
}

export const v2UIManager = new V2UIManager();